import { oneOf, readStored, writeStored } from './storage';

/**
 * Google Analytics 4: één property voor alle FerMaak-apps. Elke app gebruikt dezelfde ID,
 * zodat je alle subdomeinen op één plek ziet. Leeg = analytics staat uit (lokaal, tests).
 * Vul hier het Meet-ID in (G-XXXXXXXXXX) uit GA: Beheer > Gegevensstreams.
 */
export const GA_ID: string = 'G-FM45BKMWE5';

const CONSENT_KEY = 'fm-analytics';
export type Consent = 'granted' | 'denied' | 'unset';

const listeners = new Set<() => void>();
let loaded = false;

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
  }
}

export const analyticsEnabled = () => GA_ID !== '';

export function getConsent(): Consent {
  return readStored<Consent>(CONSENT_KEY, 'unset', oneOf(['granted', 'denied']));
}

export function subscribeConsent(fn: () => void) {
  listeners.add(fn);
  return () => void listeners.delete(fn);
}

export function setConsent(value: 'granted' | 'denied') {
  writeStored(CONSENT_KEY, value);
  if (value === 'granted') initAnalytics();
  else window.gtag?.('consent', 'update', { analytics_storage: 'denied' });
  listeners.forEach((fn) => fn());
}

/** Eigen weergave-pad: bv. '/instellingen' voor een subpagina of scherm zonder router. */
export function trackPage(path = location.pathname + location.search, title = document.title) {
  if (!loaded) return;
  window.gtag?.('event', 'page_view', {
    page_path: path,
    page_location: location.origin + path,
    page_title: title,
    app: location.hostname,
  });
}

/** Laadt GA pas na toestemming; zonder toestemming gaat er niets naar Google. */
export function initAnalytics() {
  if (!analyticsEnabled() || loaded || getConsent() !== 'granted') return;
  loaded = true;
  window.dataLayer = window.dataLayer || [];
  window.gtag = function () {
    // gtag verwacht echte `arguments`-objecten, geen arrays.
    // eslint-disable-next-line prefer-rest-params
    window.dataLayer!.push(arguments);
  };
  window.gtag('js', new Date());
  window.gtag('config', GA_ID, {
    send_page_view: false, // we sturen zelf, ook bij schermwissels zonder router
    anonymize_ip: true,
    cookie_domain: 'auto', // GA kiest fermaak.nl: één bezoeker over alle subdomeinen
    cookie_flags: 'SameSite=Lax;Secure',
  });
  const s = document.createElement('script');
  s.async = true;
  s.src = `https://www.googletagmanager.com/gtag/js?id=${GA_ID}`;
  document.head.appendChild(s);
  trackPage();
  // Navigatie met de History API of hash telt ook als paginaweergave.
  window.addEventListener('popstate', () => trackPage());
  window.addEventListener('hashchange', () => trackPage());
}
